// apps/frontend/src/components/notifications/NotificationBell.tsx
// 🔔 Cloche notifications avec dropdown

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Bell,
  CheckCheck,
  ExternalLink,
  Loader2,
  MessageCircle,
  Package,
  CreditCard,
  DollarSign,
  CheckCircle2,
  XCircle,
  Heart,
  PartyPopper,
  Bookmark,
  Users,
  User,
  Gift,
  CalendarDays,
  TrendingDown,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useNotifications,
  type NotificationPayload,
} from '../../hooks/useNotifications';
import PushPermissionPrompt from './PushPermissionPrompt';

import { glowpNavigate } from '../../lib/navigate';
// ─── Icône par type ───────────────────────────────────────────────────────────

const TYPE_ICON: Record<string, React.FC<{ className?: string }>> = {
  MESSAGE: MessageCircle,
  ORDER: Package,
  PAYMENT: CreditCard,
  OFFER_RECEIVED: DollarSign,
  OFFER_ACCEPTED: CheckCircle2,
  OFFER_REJECTED: XCircle,
  PRODUCT_LIKED: Heart,
  PRODUCT_SOLD: PartyPopper,
  PRODUCT_RESERVED: Bookmark,
  SHOP_FOLLOWED: Users,
  USER_FOLLOWED: User,
  PROMOTION: Gift,
  SYSTEM: Bell,
  EVENT_REMINDER: CalendarDays,
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "À l'instant";
  if (m < 60) return `Il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Il y a ${h}h`;
  const d = Math.floor(h / 24);
  return `Il y a ${d}j`;
}

// ─── Row ──────────────────────────────────────────────────────────────────────

const NotifRow: React.FC<{
  notif: NotificationPayload;
  onMarkRead: (id: string) => void;
  onNavigate: (url?: string) => void;
}> = ({ notif, onMarkRead, onNavigate }) => {
  const isPriceDrop = (notif as any).category === 'PRICE_DROP';
  const IconComponent = isPriceDrop
    ? TrendingDown
    : (TYPE_ICON[notif.type] ?? Bell);

  const handleClick = () => {
    if (!notif.isRead) onMarkRead(notif.id);
    if (notif.actionUrl) onNavigate(notif.actionUrl);
  };

  return (
    <div
      onClick={handleClick}
      className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-brand-light border-b border-[#FEFAF5] last:border-0 ${
        !notif.isRead
          ? isPriceDrop
            ? 'bg-brand-offwhite'
            : 'bg-[#FBF5EE]/40'
          : ''
      }`}
    >
      {/* Icon */}
      <div
        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-base shadow-sm"
        style={
          isPriceDrop
            ? { backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }
            : { backgroundColor: 'white', border: '1px solid #F3F4F6' }
        }
      >
        {notif.imageUrl ? (
          <img
            src={notif.imageUrl}
            alt=""
            className="w-full h-full object-cover rounded-full"
          />
        ) : (
          <IconComponent className="w-4 h-4 text-brand-500" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm leading-snug ${!notif.isRead ? 'font-semibold text-brand-dark' : 'text-brand-dark'}`}
        >
          {notif.title}
        </p>
        <p className="text-xs text-brand-warm mt-0.5 line-clamp-2">
          {notif.message}
        </p>
        <p className="text-xs text-brand-muted mt-1">
          {timeAgo(notif.createdAt)}
        </p>
      </div>

      {/* Unread dot */}
      {!notif.isRead && (
        <div
          className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5"
          style={{
            backgroundColor: isPriceDrop ? 'var(--brand-500)' : '#6366F1',
          }}
        />
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const NotificationBell: React.FC = () => {
  const {
    notifications,
    unreadCount,
    isLoading,
    markRead,
    markAllRead,
    fetchMore,
    hasMore,
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleNavigate = useCallback(
    (url?: string) => {
      if (!url) return;
      setOpen(false);
      // Messages utilisent le hash router
      if (url.startsWith('/messages/')) {
        glowpNavigate(url);
      } else if (url.startsWith('/')) {
        navigate(url);
      } else {
        window.open(url, '_blank', 'noopener');
      }
    },
    [navigate]
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-2 rounded-xl text-brand-warm hover:text-brand-500 hover:bg-brand-light transition-all duration-200"
        aria-label={`Notifications${unreadCount > 0 ? ` — ${unreadCount} non lues` : ''}`}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-red-500 text-white rounded-full border-2 border-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl border border-brand-border shadow-2xl z-50 overflow-hidden animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-brand-border">
            <h3 className="font-semibold text-brand-dark text-sm">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-xs bg-brand-light text-brand-warm rounded-full px-2 py-0.5">
                  {unreadCount} non lues
                </span>
              )}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs text-brand-warm hover:text-brand-warm font-medium transition-colors"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Tout lire
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-brand-muted">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                <span className="text-sm">Chargement...</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                <Bell className="w-10 h-10 text-brand-border mb-3" />
                <p className="text-sm font-medium text-brand-dark">
                  Aucune notification
                </p>
                <p className="text-xs text-brand-muted mt-1">
                  Tes notifications apparaîtront ici
                </p>
              </div>
            ) : (
              <>
                {notifications.map(n => (
                  <NotifRow
                    key={n.id}
                    notif={n}
                    onMarkRead={markRead}
                    onNavigate={handleNavigate}
                  />
                ))}
                {hasMore && (
                  <button
                    onClick={fetchMore}
                    disabled={isLoading}
                    className="w-full py-3 text-xs text-brand-warm hover:text-brand-warm font-medium border-t border-brand-border transition-colors disabled:opacity-50"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin inline mr-1" />
                    ) : null}
                    Voir plus
                  </button>
                )}
              </>
            )}
          </div>

          {/* Push permission prompt */}
          <PushPermissionPrompt />

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2.5 border-t border-brand-border flex justify-end">
              <button
                onClick={() => {
                  setOpen(false);
                  navigate('/account');
                }}
                className="flex items-center gap-1 text-xs text-brand-warm hover:text-brand-dark transition-colors"
              >
                Gérer les préférences
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
